import './dashboard-layout.css';
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client.js';
import { useApiQuery } from '../../hooks.js';
import { Button, Loading, Panel } from '../../components/ui.js';

export type DashboardTab = 'federal' | 'state';
export interface DashboardSectionLayout {
  section_order: string[];
  collapsed_sections: string[];
}
export interface DashboardLayoutsResponse {
  layouts: Record<DashboardTab, DashboardSectionLayout>;
  updated_at: string | null;
}
export interface DashboardSectionDefinition {
  id: string;
  title: string;
  description: string;
  content: ReactNode;
  status?: ReactNode;
}

export const dashboardSectionIds: Record<DashboardTab, string[]> = {
  federal: [
    'federal-agent-controls', 'federal-processing-settings',
    'federal-candidate-progress', 'federal-current-batch-progress', 'federal-parallel-candidates',
    'federal-pipeline', 'federal-inventory', 'federal-coverage', 'federal-activity-14d',
    'federal-government-apis', 'federal-election-source', 'federal-activity-terminal',
    'federal-collection-logs', 'federal-retries', 'federal-recent-jobs', 'federal-access-analytics', 'federal-suggestions',
  ],
  state: ['state-coverage-summary', 'state-agent-status'],
};

const defaultLayouts: Record<DashboardTab, DashboardSectionLayout> = {
  federal: { section_order: [...dashboardSectionIds.federal], collapsed_sections: [] },
  state: { section_order: [...dashboardSectionIds.state], collapsed_sections: [] },
};

function normalizeSectionLayout(value: unknown, tab: DashboardTab): DashboardSectionLayout {
  const source = value && typeof value === 'object' ? value as Partial<DashboardSectionLayout> : {};
  const allowed = dashboardSectionIds[tab];
  const savedOrder = Array.isArray(source.section_order) ? source.section_order : [];
  const section_order = [...new Set(savedOrder.filter((id): id is string => typeof id === 'string' && allowed.includes(id)))];
  for (const id of allowed) if (!section_order.includes(id)) section_order.push(id);
  const savedCollapsed = Array.isArray(source.collapsed_sections) ? source.collapsed_sections : [];
  const collapsed_sections = [...new Set(savedCollapsed.filter((id): id is string => typeof id === 'string' && allowed.includes(id)))];
  return { section_order, collapsed_sections };
}

function normalizeLayouts(value: DashboardLayoutsResponse | undefined): Record<DashboardTab, DashboardSectionLayout> {
  return {
    federal: normalizeSectionLayout(value?.layouts?.federal, 'federal'),
    state: normalizeSectionLayout(value?.layouts?.state, 'state'),
  };
}

export function useDashboardLayout(enabled: boolean) {
  const savedLayout = useApiQuery<DashboardLayoutsResponse>(['dashboard-layout'], '/api/dashboard/layout', enabled, { staleTime: 60_000 });
  const queryClient = useQueryClient();
  const [layouts, setLayouts] = useState(defaultLayouts);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('Carregando preferências da base…');
  const version = useRef(0);
  const latestVersion = useRef<Record<DashboardTab, number>>({ federal: 0, state: 0 });
  const saveStatus = useRef<Record<DashboardTab, 'saved' | 'pending' | 'error'>>({ federal: 'saved', state: 'saved' });
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const mutation = useMutation({
    mutationFn: ({ tab, layout }: { tab: DashboardTab; layout: DashboardSectionLayout; version: number }) => api<DashboardLayoutsResponse>('/api/dashboard/layout', {
      method: 'PUT',
      body: JSON.stringify({ tab, ...layout }),
    }),
    onSuccess: (result) => queryClient.setQueryData(['dashboard-layout'], result),
  });

  const publishSaveStatus = () => {
    const failedTabs = (['federal', 'state'] as const).filter((tab) => saveStatus.current[tab] === 'error');
    if (failedTabs.length) {
      setMessage(`Não foi possível salvar as preferências da aba ${failedTabs.join(' e ')}.`);
    } else if ((['federal', 'state'] as const).some((tab) => saveStatus.current[tab] === 'pending')) {
      setMessage('Salvando preferências na base…');
    } else {
      setMessage('Preferências salvas na base de dados.');
    }
  };

  const enqueueSave = (payload: { tab: DashboardTab; layout: DashboardSectionLayout; version: number }) => {
    saveStatus.current[payload.tab] = 'pending';
    saveQueue.current = saveQueue.current
      .catch(() => undefined)
      .then(() => mutation.mutateAsync(payload))
      .then(() => {
        if (payload.version === latestVersion.current[payload.tab]) saveStatus.current[payload.tab] = 'saved';
        publishSaveStatus();
      })
      .catch(() => {
        if (payload.version === latestVersion.current[payload.tab]) saveStatus.current[payload.tab] = 'error';
        publishSaveStatus();
      });
  };

  useEffect(() => {
    if (!enabled) {
      setLayouts(defaultLayouts);
      setReady(false);
      setMessage('Carregando preferências da base…');
      return;
    }
    if (!ready && savedLayout.data) {
      setLayouts(normalizeLayouts(savedLayout.data));
      setReady(true);
      setMessage('Preferências carregadas da base de dados.');
    } else if (!ready && savedLayout.error) {
      setMessage('Não foi possível carregar as preferências da base.');
    }
  }, [enabled, ready, savedLayout.data, savedLayout.error]);

  const updateLayout = (tab: DashboardTab, layout: DashboardSectionLayout) => {
    if (!ready) return;
    const currentVersion = ++version.current;
    latestVersion.current[tab] = currentVersion;
    saveStatus.current[tab] = 'pending';
    setLayouts((current) => ({ ...current, [tab]: layout }));
    setMessage('Salvando preferências na base…');
    enqueueSave({ tab, layout, version: currentVersion });
  };

  return { layouts, ready, loadError: Boolean(savedLayout.error && !savedLayout.data), message, updateLayout };
}

export function DashboardLayout({
  tab,
  sections,
  layout,
  ready,
  loadError,
  saveMessage,
  onLayoutChange,
}: {
  tab: DashboardTab;
  sections: DashboardSectionDefinition[];
  layout: DashboardSectionLayout;
  ready: boolean;
  loadError: boolean;
  saveMessage: string;
  onLayoutChange: (tab: DashboardTab, layout: DashboardSectionLayout) => void;
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const startSectionDrag = (event: DragEvent<HTMLElement>, id: string) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
    setDraggingId(id);
  };
  const finishSectionDrag = () => { setDraggingId(null); setDropTargetId(null); };
  const sectionById = new Map(sections.map((section) => [section.id, section]));
  const orderedIds = layout.section_order.filter((id) => sectionById.has(id));
  for (const section of sections) if (!orderedIds.includes(section.id)) orderedIds.push(section.id);

  const replaceOrder = (sourceId: string, targetId: string, after: boolean) => {
    if (sourceId === targetId) return;
    const next = [...orderedIds];
    const sourceIndex = next.indexOf(sourceId);
    const targetIndex = next.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    next.splice(sourceIndex, 1);
    const adjustedTargetIndex = next.indexOf(targetId);
    next.splice(adjustedTargetIndex + (after ? 1 : 0), 0, sourceId);
    if (next.some((id, index) => id !== orderedIds[index])) onLayoutChange(tab, { ...layout, section_order: next });
  };

  const moveBy = (id: string, offset: number) => {
    const index = orderedIds.indexOf(id);
    const target = index + offset;
    if (target < 0 || target >= orderedIds.length) return;
    const next = [...orderedIds];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onLayoutChange(tab, { ...layout, section_order: next });
  };

  const toggleCollapsed = (id: string) => {
    const collapsed = new Set(layout.collapsed_sections);
    if (collapsed.has(id)) collapsed.delete(id); else collapsed.add(id);
    onLayoutChange(tab, { ...layout, collapsed_sections: [...collapsed] });
  };

  const onDrop = (event: DragEvent<HTMLElement>, targetId: string) => {
    event.preventDefault();
    if (!draggingId) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    replaceOrder(draggingId, targetId, event.clientY > bounds.top + bounds.height / 2);
    setDraggingId(null);
    setDropTargetId(null);
  };

  return <div className="dashboard-layout" data-dashboard-tab={tab}>
    <div className="dashboard-layout-toolbar">
      <p>Arraste o título para reorganizar; use os controles para mover, recolher ou expandir cada bloco.</p>
      <span role="status" aria-live="polite">{ready ? saveMessage : loadError ? 'Preferências indisponíveis; organização desabilitada.' : 'Carregando preferências da base…'}</span>
    </div>
    {!ready && (loadError
      ? <p className="dashboard-layout-load-error" role="alert">Não foi possível carregar a organização salva. A reordenação está desabilitada para proteger as preferências existentes.</p>
      : <Loading label="Restaurando a organização salva…" />)}
    <div className="dashboard-layout-sections" aria-label={tab === 'federal' ? 'Seções do painel federal' : 'Seções do painel estadual'}>
      {orderedIds.map((id, index) => {
        const section = sectionById.get(id);
        if (!section) return null;
        const collapsed = layout.collapsed_sections.includes(id);
        return <section
          key={id}
          id={id}
          className={`dashboard-layout-card${draggingId === id ? ' is-dragging' : ''}${dropTargetId === id ? ' is-drop-target' : ''}`}
          aria-labelledby={`${id}-heading`}
          onDragOver={(event) => { if (draggingId && draggingId !== id) { event.preventDefault(); setDropTargetId(id); } }}
          onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropTargetId(null); }}
          onDrop={(event) => onDrop(event, id)}
        >
          <Panel className="dashboard-layout-panel">
            <header
              className="dashboard-layout-heading"
              onClick={(event) => {
                if (!ready || (event.target as HTMLElement).closest('button')) return;
                toggleCollapsed(id);
              }}
            >
              <div className="dashboard-layout-heading-copy">
                <h2
                  id={`${id}-heading`}
                  draggable={ready}
                  onDragStart={(event) => startSectionDrag(event, id)}
                  onDragEnd={finishSectionDrag}
                  title={ready ? 'Arraste este título para reorganizar os blocos' : undefined}
                >
                  <button
                    type="button"
                    aria-expanded={!collapsed}
                    aria-controls={`${id}-content`}
                    disabled={!ready}
                    onClick={() => toggleCollapsed(id)}
                  >{section.title}</button>
                </h2>
                {section.description && <p>{section.description}</p>}
              </div>
              {section.status && <div className="dashboard-layout-status">{section.status}</div>}
              <div className="dashboard-layout-controls" aria-label={`Controles de ${section.title}`}>
                <Button variant="unstyled" className="dashboard-layout-grip" type="button" draggable={ready} title={`Arraste para mover ${section.title}`} aria-label={`Arraste para mover ${section.title}`} disabled={!ready} onDragStart={(event) => startSectionDrag(event, id)} onDragEnd={finishSectionDrag}>⠿</Button>
                <Button variant="unstyled" className="dashboard-layout-move" type="button" title="Mover para cima" aria-label={`Mover ${section.title} para cima`} disabled={!ready || index === 0} onClick={() => moveBy(id, -1)}>↑</Button>
                <Button variant="unstyled" className="dashboard-layout-move" type="button" title="Mover para baixo" aria-label={`Mover ${section.title} para baixo`} disabled={!ready || index === orderedIds.length - 1} onClick={() => moveBy(id, 1)}>↓</Button>
                <Button variant="unstyled" className="dashboard-layout-collapse" type="button" aria-label={collapsed ? `Expandir ${section.title}` : `Recolher ${section.title}`} aria-expanded={!collapsed} aria-controls={`${id}-content`} disabled={!ready} onClick={() => toggleCollapsed(id)}>{collapsed ? 'Expandir' : 'Recolher'} <span aria-hidden="true">{collapsed ? '›' : '⌄'}</span></Button>
              </div>
            </header>
            <div className="dashboard-layout-body" id={`${id}-content`} hidden={collapsed}>{section.content}</div>
          </Panel>
        </section>;
      })}
    </div>
  </div>;
}
